import {
    ServiceTemplateScheduleOccurrencesColumn,
    ServiceTemplateScheduleOccurrencesSortValue,
} from "@common/types/views/serviceTemplateScheduleOccurrencesView";
import { ServiceTemplateRecurrenceOccurrenceStatus } from "@common/types/serviceTemplateRecurrenceOccurrence";
import { ViewConfig } from "@lib/views";
import { ServiceTemplateScheduleOccurrenceIndexCell } from "./cells/ServiceTemplateScheduleOccurrenceIndexCell";
import { ServiceTemplateScheduleOccurrenceDateCell } from "./cells/ServiceTemplateScheduleOccurrenceDateCell";
import { ServiceTemplateScheduleOccurrencePreparerCell } from "./cells/ServiceTemplateScheduleOccurrencePreparerCell";
import { ServiceTemplateScheduleOccurrenceReviewerCell } from "./cells/ServiceTemplateScheduleOccurrenceReviewerCell";
import { ServiceTemplateScheduleOccurrenceManagerCell } from "./cells/ServiceTemplateScheduleOccurrenceManagerCell";
import { ServiceTemplateScheduleOccurrenceClientCell } from "./cells/ServiceTemplateScheduleOccurrenceClientCell";
import { ServiceTemplateScheduleOccurrenceStatusCell } from "./cells/ServiceTemplateScheduleOccurrenceStatusCell";
import { ServiceTemplateScheduleOccurrenceActionsCell } from "./cells/ServiceTemplateScheduleOccurrenceActionsCell";
import { ServiceTemplateScheduleOccurrenceEstimatedTimeCell } from "./cells/ServiceTemplateScheduleOccurrenceEstimatedTimeCell";

// Larger, uniform min/max bounds (vs. the shared DATE_COLUMN_SIZE) so the
// date columns expand evenly to fill the available table width instead of
// staying pinned to a narrow fixed size.
const OCCURRENCE_DATE_COLUMN_SIZE = {
    minSize: 140,
    maxSize: 220,
};

const OCCURRENCE_MEMBER_COLUMN_SIZE = {
    minSize: 180,
    maxSize: 260,
};

/**
 * Shared row shape for both the read-only schedule-wizard preview (dates
 * only, no persisted occurrence yet) and the "Upcoming occurrences" drawer
 * (real, persisted occurrences with role assignments and status).
 */
export interface ServiceTemplateScheduleOccurrenceRow {
    id: string;
    index: number;
    applicationDate: string;
    serviceStartDate: string;
    serviceEndDate: string;
    workStartDate: string;
    workEndDate: string;
    preparerId?: number | null;
    reviewerId?: number | null;
    managerId?: number | null;
    clientId?: number | null;
    status?: ServiceTemplateRecurrenceOccurrenceStatus;
    /** Set once the occurrence has been applied and produced a close (work). Null when the resulting close was later deleted. */
    closeId?: string | null;
    /** Workspace the applied close belongs to; needed alongside `closeId` to open the work row. */
    workspaceId?: string | null;
}

export const serviceTemplateScheduleOccurrencesViewConfig: ViewConfig<
    ServiceTemplateScheduleOccurrenceRow,
    ServiceTemplateScheduleOccurrencesColumn,
    ServiceTemplateScheduleOccurrencesSortValue
> = {
    columns: {
        [ServiceTemplateScheduleOccurrencesColumn.INDEX]: {
            accessorKey: "index",
            name: "#",
            header: "#",
            immutable: true,
            cell: ServiceTemplateScheduleOccurrenceIndexCell,
            sort: {
                asc: ServiceTemplateScheduleOccurrencesSortValue.INDEX_ASC,
                desc: ServiceTemplateScheduleOccurrencesSortValue.INDEX_DESC,
                defaultOrder: "asc",
            },
            minSize: 50,
            maxSize: 60,
        },
        [ServiceTemplateScheduleOccurrencesColumn.APPLICATION_DATE]: {
            accessorKey: "applicationDate",
            name: "Application date",
            header: "Application date",
            cell: ServiceTemplateScheduleOccurrenceDateCell,
            sort: {
                asc: ServiceTemplateScheduleOccurrencesSortValue.APPLICATION_DATE_ASC,
                desc: ServiceTemplateScheduleOccurrencesSortValue.APPLICATION_DATE_DESC,
            },
            ...OCCURRENCE_DATE_COLUMN_SIZE,
        },
        [ServiceTemplateScheduleOccurrencesColumn.SERVICE_START]: {
            accessorKey: "serviceStartDate",
            name: "Service start",
            header: "Service start",
            cell: ServiceTemplateScheduleOccurrenceDateCell,
            sort: {
                asc: ServiceTemplateScheduleOccurrencesSortValue.SERVICE_START_ASC,
                desc: ServiceTemplateScheduleOccurrencesSortValue.SERVICE_START_DESC,
            },
            ...OCCURRENCE_DATE_COLUMN_SIZE,
        },
        [ServiceTemplateScheduleOccurrencesColumn.SERVICE_END]: {
            accessorKey: "serviceEndDate",
            name: "Service end",
            header: "Service end",
            cell: ServiceTemplateScheduleOccurrenceDateCell,
            sort: {
                asc: ServiceTemplateScheduleOccurrencesSortValue.SERVICE_END_ASC,
                desc: ServiceTemplateScheduleOccurrencesSortValue.SERVICE_END_DESC,
            },
            ...OCCURRENCE_DATE_COLUMN_SIZE,
        },
        [ServiceTemplateScheduleOccurrencesColumn.WORK_START]: {
            accessorKey: "workStartDate",
            name: "Work start",
            header: "Work start",
            cell: ServiceTemplateScheduleOccurrenceDateCell,
            sort: {
                asc: ServiceTemplateScheduleOccurrencesSortValue.WORK_START_ASC,
                desc: ServiceTemplateScheduleOccurrencesSortValue.WORK_START_DESC,
            },
            ...OCCURRENCE_DATE_COLUMN_SIZE,
        },
        [ServiceTemplateScheduleOccurrencesColumn.WORK_END]: {
            accessorKey: "workEndDate",
            name: "Work end",
            header: "Work end",
            cell: ServiceTemplateScheduleOccurrenceDateCell,
            sort: {
                asc: ServiceTemplateScheduleOccurrencesSortValue.WORK_END_ASC,
                desc: ServiceTemplateScheduleOccurrencesSortValue.WORK_END_DESC,
            },
            ...OCCURRENCE_DATE_COLUMN_SIZE,
        },
        [ServiceTemplateScheduleOccurrencesColumn.PREPARER]: {
            accessorKey: "preparerId",
            name: "Preparer",
            header: "Preparer",
            cell: ServiceTemplateScheduleOccurrencePreparerCell,
            ...OCCURRENCE_MEMBER_COLUMN_SIZE,
        },
        [ServiceTemplateScheduleOccurrencesColumn.REVIEWER]: {
            accessorKey: "reviewerId",
            name: "Reviewer",
            header: "Reviewer",
            cell: ServiceTemplateScheduleOccurrenceReviewerCell,
            ...OCCURRENCE_MEMBER_COLUMN_SIZE,
        },
        [ServiceTemplateScheduleOccurrencesColumn.MANAGER]: {
            accessorKey: "managerId",
            name: "Manager",
            header: "Manager",
            cell: ServiceTemplateScheduleOccurrenceManagerCell,
            ...OCCURRENCE_MEMBER_COLUMN_SIZE,
        },
        [ServiceTemplateScheduleOccurrencesColumn.CLIENT]: {
            accessorKey: "clientId",
            name: "Client",
            header: "Client",
            cell: ServiceTemplateScheduleOccurrenceClientCell,
            ...OCCURRENCE_MEMBER_COLUMN_SIZE,
        },
        [ServiceTemplateScheduleOccurrencesColumn.ESTIMATED_TIME]: {
            // Derived from the schedule's template tasks via table meta.
            accessorKey: "estimatedTime",
            name: "Estimated time",
            header: "Estimated time",
            cell: ServiceTemplateScheduleOccurrenceEstimatedTimeCell,
            align: "right",
            minSize: 140,
            maxSize: 200,
        },
        [ServiceTemplateScheduleOccurrencesColumn.STATUS]: {
            accessorKey: "status",
            name: "Status",
            header: "Status",
            immutable: true,
            cell: ServiceTemplateScheduleOccurrenceStatusCell,
            sort: {
                asc: ServiceTemplateScheduleOccurrencesSortValue.STATUS_ASC,
                desc: ServiceTemplateScheduleOccurrencesSortValue.STATUS_DESC,
            },
            minSize: 130,
            maxSize: 150,
        },
        [ServiceTemplateScheduleOccurrencesColumn.ACTIONS]: {
            accessorKey: "status",
            name: "Actions",
            header: "",
            immutable: true,
            hiddenFromSettings: true,
            cell: ServiceTemplateScheduleOccurrenceActionsCell,
            minSize: 60,
            maxSize: 60,
        },
    },
};
